import { AlertBanner } from '@c42/component-library';
import { StsEmployeeDetails } from '@c42/jwt/dist/constants';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import * as React from 'react';
import Text from '../../../shared/Text';
import dayjs from 'dayjs';

interface Props {
  employeeDetails?: StsEmployeeDetails;
  notFound: boolean;
  error: boolean;
}

const formatDate = (iso: string): string => {
  const parsed = dayjs.utc(iso);
  return parsed.isValid() ? parsed.local().format('MMM D, YYYY, hh:mm A') : iso;
};

const loginTypeLabel = (loginType?: string | null): string => {
  if (loginType === 'OKTA') return Text.get('audit_log_login_type_okta');
  if (loginType === 'MIMECAST_LOCAL') return Text.get('audit_log_login_type_mimecast_local');
  return loginType ?? '—';
};

const LoginAuditLogResults: React.FC<Props> = ({ employeeDetails, notFound, error }) => {
  const isEmpty =
    notFound || (employeeDetails && employeeDetails.recentTenants.length === 0);

  return (
    <Paper elevation={1}>
      <Box p={3}>
        {error && (
          <Box mb={2}>
            <AlertBanner type="error" variant="standard">
              {Text.get('audit_log_lookup_error')}
            </AlertBanner>
          </Box>
        )}

        {employeeDetails && (
          <Box mb={2}>
            <Typography>
              <strong>{employeeDetails.employeeId}</strong>
            </Typography>
            <Typography>{employeeDetails.email}</Typography>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{Text.get('audit_log_col_tenant_id')}</TableCell>
                <TableCell>{Text.get('audit_log_col_tenant_name')}</TableCell>
                <TableCell>{Text.get('audit_log_col_account_code')}</TableCell>
                <TableCell>{Text.get('audit_log_col_login_type')}</TableCell>
                <TableCell>{Text.get('audit_log_col_last_login')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty && (
                <TableRow>
                  <TableCell colSpan={5}>{Text.get('audit_log_no_results')}</TableCell>
                </TableRow>
              )}
              {!isEmpty &&
                employeeDetails?.recentTenants.map((tenant) => (
                  <TableRow key={tenant.tenantId}>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{tenant.tenantId}</TableCell>
                    <TableCell>{tenant.tenantName ?? '—'}</TableCell>
                    <TableCell>{tenant.x1AccountCode ?? '—'}</TableCell>
                    <TableCell>{loginTypeLabel(tenant.loginType)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tenant.lastUsed)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Paper>
  );
};

export default LoginAuditLogResults;
